import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../../styles/theme';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  color?: 'primary' | 'secondary' | 'accent' | 'success' | 'warning';
  trend?: {
    direction: 'up' | 'down' | 'stable';
    value: string;
  };
}

const CardContainer = styled(motion.div)<{ color: string }>`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.md};
  border-left: 4px solid ${({ color }) => {
    switch (color) {
      case 'primary': return theme.colors.primary;
      case 'secondary': return theme.colors.secondary;
      case 'accent': return theme.colors.accent;
      case 'success': return '#10B981';
      case 'warning': return '#F59E0B';
      default: return theme.colors.primary;
    }
  }};
  
  &:hover {
    box-shadow: ${theme.shadows.lg};
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
`;

const CardTitle = styled.h4`
  color: ${theme.colors.gray[600]};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
`;

const CardIcon = styled.div<{ color: string }>`
  color: ${({ color }) => {
    switch (color) {
      case 'primary': return theme.colors.primary;
      case 'secondary': return theme.colors.secondary;
      case 'accent': return theme.colors.accent;
      case 'success': return '#10B981';
      case 'warning': return '#F59E0B';
      default: return theme.colors.primary;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardValue = styled.div`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.gray[900]};
  margin-bottom: ${theme.spacing.xs};
`;

const CardSubtitle = styled.div`
  color: ${theme.colors.gray[500]};
  font-size: ${theme.typography.sizes.sm};
`;

const TrendIndicator = styled.div<{ direction: string }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  color: ${({ direction }) => {
    switch (direction) {
      case 'up': return '#10B981';
      case 'down': return '#EF4444';
      case 'stable': return theme.colors.gray[500];
      default: return theme.colors.gray[500];
    }
  }};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  margin-top: ${theme.spacing.xs};
`;

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color = 'primary',
  trend
}) => {
  return (
    <CardContainer
      color={color}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {icon && <CardIcon color={color}>{icon}</CardIcon>}
      </CardHeader>
      
      <CardValue>{value}</CardValue>
      
      {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
      
      {trend && (
        <TrendIndicator direction={trend.direction}>
          {trend.direction === 'up' && '↗'}
          {trend.direction === 'down' && '↘'}
          {trend.direction === 'stable' && '→'}
          {trend.value}
        </TrendIndicator>
      )}
    </CardContainer>
  );
};